import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";

import { isOrderExpired } from "@helpers/orders/cart";

import {
  treatExpired,
  treatNotFound,
  MESSAGE_THRESHOLD,
  DONT_SHOW_VIDEO_FOR_ERRORS,
} from "@constants/orders/rlp";

export const formatRlpOrder = (order = {}) => {
  if (!order?.spreeOrganizerOrders) return null;
  const spreeOrganizerOrders = Object.values(order.spreeOrganizerOrders)[0];

  if (order?.spreeVideoMessages) {
    spreeOrganizerOrders.videoMessage = Object.values(order?.spreeVideoMessages)[0];
  }

  if (order?.spreeGiftDetails) {
    spreeOrganizerOrders.giftDetail = Object.values(order.spreeGiftDetails)[0];
  }
  if (order?.spreeAvailableBundles) {
    const availableKitsCount = Object.values(order.spreeAvailableBundles);

    spreeOrganizerOrders.availableKitsCount = keyBy(availableKitsCount, "zoneId");
  }

  if (order?.spreeMessages) {
    spreeOrganizerOrders.senderRlpMessage = Object.values(order.spreeMessages)[0];
  }

  return spreeOrganizerOrders;
};

export const getAskEmailOrderError = (orderFetched, order) => {
  if (!orderFetched) return null;
  if (order) return isOrderExpired(order) ? treatExpired : null;
  return treatNotFound;
};

/** Merges RLP thank-you skip flag onto the redeem response (no organizerOrder in eshop RLP). */
export const mergeRecipientOrderWithThankYouPromptSettings = (
  recipientOrder,
  hideThankYouMessagePrompt,
) => ({
  ...recipientOrder,
  hideThankYouMessagePrompt,
});

const getAskEmailMessageDisplay = (order) => {
  const rlpMessage = order?.rlpMessage || "";
  const isMessageLengthy = rlpMessage.length > MESSAGE_THRESHOLD * 1.2;
  return {
    rlpMessage,
    isMessageLengthy,
    truncatedText: isMessageLengthy ? rlpMessage.slice(0, MESSAGE_THRESHOLD) : rlpMessage,
  };
};

const getAskEmailSenderDisplay = (order) => ({
  embeddedCode: order?.videoMessage?.embeddedCode || "",
  senderName: order?.giftDetail?.senderName || "",
  pricelessItemCount: order?.pricelessItemCount ?? "",
});

const shouldShowAskEmailVideo = (embeddedCode, error) =>
  embeddedCode.length > 0 && !DONT_SHOW_VIDEO_FOR_ERRORS.includes(error);

export const getAskEmailDisplayState = (order, orderFetched, formError) => {
  const error = formError ?? getAskEmailOrderError(orderFetched, order);
  const { embeddedCode, senderName, pricelessItemCount } = getAskEmailSenderDisplay(order);

  return {
    error,
    ...getAskEmailMessageDisplay(order),
    embeddedCode,
    senderName,
    pricelessItemCount,
    showVideo: shouldShowAskEmailVideo(embeddedCode, error),
  };
};

export const checkCountryWiseKitStock = ({ countryWiseLineItems }) => {
  const { withInventory: warehouseProducts = [] } = countryWiseLineItems || {};

  if (isEmpty(warehouseProducts)) {
    return false;
  }

  return warehouseProducts.some(({ total, quantity }) => {
    const { available } = total || {};
    return available < quantity;
  });
};

const checkKitZoneAvailability = (availableKitsCount, zoneId) =>
  !!availableKitsCount[zoneId]?.availableCount;

export const checkKitCountAvailability = ({
  availableKitsCount,
  selectedCountry,
  sendUsToNonUs,
  usZoneData,
  globalZoneData,
}) =>
  checkKitZoneAvailability(availableKitsCount, selectedCountry.zoneId) ||
  (!(selectedCountry.zoneId in availableKitsCount) &&
    checkKitZoneAvailability(availableKitsCount, globalZoneData.zoneId)) ||
  (sendUsToNonUs && checkKitZoneAvailability(availableKitsCount, usZoneData.zoneId));
